/**
 * Lightweight DOM helpers + toast + modal system.
 */

export function el(tag, attrs = {}, children = []) {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v == null || v === false) continue;
    if (k === 'class') node.className = v;
    else if (k === 'style' && typeof v === 'object') Object.assign(node.style, v);
    else if (k === 'value') node.value = v;
    else if (k.startsWith('on') && typeof v === 'function') {
      node.addEventListener(k.slice(2).toLowerCase(), v);
    }
    else if (k === 'html') node.innerHTML = v;
    else node.setAttribute(k, v === true ? '' : v);
  }
  for (const child of [].concat(children)) {
    if (child == null || child === false) continue;
    node.appendChild(child instanceof Node ? child : document.createTextNode(String(child)));
  }
  return node;
}

export function clear(node) { while (node.firstChild) node.removeChild(node.firstChild); }

/* -------- Toast -------- */

let toastId = 0;
export function toast(message, kind = 'info', duration = 3500) {
  const host = document.getElementById('toast-host');
  if (!host) return;
  const id = ++toastId;
  const node = el('div', { class: `toast ${kind}`, 'data-toast-id': id }, [message]);
  host.appendChild(node);
  setTimeout(() => {
    if (node.parentNode) node.parentNode.removeChild(node);
  }, duration);
}

/* -------- Modal -------- */

const modalStack = [];

/**
 * Open a modal. The render function receives a "close" callback.
 *
 *   openModal(close => el('div', ..., [
 *     el('button', { onClick: close }, ['Close'])
 *   ]), { large: true });
 *
 * Optional 2nd arg: { large?: boolean, dismissible?: boolean }
 */
export function openModal(buildContents, options = {}) {
  const host = document.getElementById('modal-host');
  if (!host) return () => {};

  const overlay = el('div', { class: 'modal-overlay' });
  const cardClass = ['modal-card', 'standard'];
  if (options.large) cardClass.push('large');
  const card = el('div', { class: cardClass.join(' '), role: 'dialog', 'aria-modal': 'true' });

  const close = () => {
    overlay.remove();
    card.remove();
    modalStack.splice(modalStack.indexOf(close), 1);
    if (modalStack.length === 0) host.classList.remove('is-open');
    if (options.onClose) options.onClose();
  };

  if (options.dismissible !== false) {
    overlay.addEventListener('click', close);
  }

  card.appendChild(buildContents(close));

  host.appendChild(overlay);
  host.appendChild(card);
  host.classList.add('is-open');
  modalStack.push(close);

  // Esc closes top-most
  if (!host._kh) {
    host._kh = (ev) => {
      if (ev.key === 'Escape' && modalStack.length > 0) {
        const top = modalStack[modalStack.length - 1];
        top();
      }
    };
    document.addEventListener('keydown', host._kh);
  }

  return close;
}

/**
 * Special-case renderer for the coding modal — uses the editorial layout
 * (hero vendor name + form) per design plan §4.
 */
export function openCodingModal({ vendorName, eyebrow, invoiceMeta, body, primaryLabel, onPrimary, onSkip }) {
  return openModal((close) => {
    const card = el('div');

    card.appendChild(el('div', { class: 'modal-head' }, [
      eyebrow && el('div', { class: 'eyebrow' }, [eyebrow]),
      el('h1', { class: 'vendor-name' }, [vendorName || '(unknown vendor)']),
      invoiceMeta && el('div', { class: 'invoice-meta' }, [invoiceMeta])
    ]));

    card.appendChild(el('div', { class: 'modal-body' }, [body]));

    card.appendChild(el('div', { class: 'modal-foot' }, [
      el('button', {
        class: 'primary-btn',
        onClick: () => {
          const ok = onPrimary ? onPrimary(close) : true;
          if (ok !== false) close();
        }
      }, [primaryLabel || 'Save']),
      el('button', {
        class: 'skip-link',
        onClick: () => { if (onSkip) onSkip(close); else close(); }
      }, ['Skip this vendor'])
    ]));

    return card;
  }, { large: false, dismissible: false });
}
